import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import type { Range } from '@tanstack/react-virtual';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, type SortingStrategy } from '@dnd-kit/sortable';
import { classNames } from 'primereact/utils';

import { Button, BundleTreeTypes, InputNumber } from '@repo/atomic-components';
import {
  useSizes,
  useGridDragAndDrop,
  useGridKeyboardNavigation,
  useGridSelection,
  useThumbnailsViewedStatus,
  useThumbnailPageInfoPrefetch,
} from './hooks';
import {
  ContextMenuShowProps,
  GroupLabel,
  SortableThumbnailPage,
  ThumbnailItemClickProps,
  ThumbnailPageOverlay,
  ThumbnailPointerDownProps,
} from './components';
import {
  GridGroup,
  GridItem,
  GroupSectionLabel,
  HandleDropRequestProps,
  IsDropRestrictedProps,
  VirtualRowType,
} from './types';
import {
  COL_GAP,
  MAX_COL_WIDTH,
  MIN_COL_WIDTH,
  MIN_GROUP_HEIGHT,
  PAGE_NUMBER_HEIGHT,
  SCROLL_OUT_OF_THE_COMPONENT_DELAY_MS,
} from './constants';
import { calculateA4Height } from '@/utils';
import { type BundleTypeKeys } from '@/store';
import { GroupedTanStackVirtualThumbnailsListRef } from '../../hooks';

const shouldDisplayLabel = (label: GroupSectionLabel, previousLabels: GroupSectionLabel[], index: number) => {
  if (label.level === 4) return true;
  if (index >= previousLabels.length) return true;
  return label.text !== previousLabels[index].text || label.level !== previousLabels[index].level;
};

// Hold every page still during a drag: only the drag overlay and the drop marker
// move, so a large multi-page selection stays readable instead of the grid
// reshuffling under the pointer.
const staticSortingStrategy: SortingStrategy = () => null;

interface PreventAutoScrollingPageProps {
  pageIndex: number;
  pageId?: string;
}
export interface GroupedTanStackVirtualThumbnailsListProps {
  groups: GridGroup[];
  setGroups: React.Dispatch<React.SetStateAction<GridGroup[]>>;
  activeBundleType?: BundleTypeKeys | null;
  isDragAndDropDisabled?: boolean;
  selectedThumbnailPageIds?: string[];
  bundleCutOutThumbnailIndexes?: number[];
  onSelectionChange?: (selectedIds: string[]) => void;
  onThumbnailItemClick?: (props: ThumbnailItemClickProps) => void;
  onContextMenuShow?: (props: ContextMenuShowProps) => void;
  onDropRequest?: (props: HandleDropRequestProps) => void;
  isDropRestricted?: (props: IsDropRestrictedProps) => boolean;
  onDragStateChange?: (isDragging: boolean) => void;
  /** When set, cluster date labels get a hover pencil that calls this with the label. */
  onEditClusterDate?: (label: GroupSectionLabel) => void;
  /**
   * Page to centre once the grid can actually resolve it — the landing target of
   * a cross-tab "View in Full/Light Bundle" jump or a `page_id_in_viewport_on_init`
   * deep link. Retried as the layout settles rather than acted on once, and
   * honoured at most once per mount.
   */
  initialScrollToPageId?: string | null;
}

export const GroupedTanStackVirtualThumbnailsList = forwardRef<
  GroupedTanStackVirtualThumbnailsListRef,
  GroupedTanStackVirtualThumbnailsListProps
>(
  (
    {
      groups,
      setGroups,
      activeBundleType,
      isDragAndDropDisabled = false,
      selectedThumbnailPageIds = [],
      bundleCutOutThumbnailIndexes = [],
      onSelectionChange,
      onThumbnailItemClick,
      onContextMenuShow,
      onDropRequest,
      isDropRestricted,
      onDragStateChange,
      onEditClusterDate,
      initialScrollToPageId,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const preventAutoScrollingPageRef = useRef<PreventAutoScrollingPageProps | null>(null);
    const currentViewportPageIndexRef = useRef<number | null>(null);
    const prevItemWidthRef = useRef<number | null>(null);
    const prevHasMeasuredWidthRef = useRef(false);
    const resizeScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const activeStickyGroupHeaderRowIndexRef = useRef<number>(0);

    const lastScrollToIndexTimeRef = useRef<number>(0);
    const lastScrollToIdTimeRef = useRef<number>(0);

    const hasHandledInitialScrollRef = useRef(false);

    // Mirrored so the handlers handed to every thumbnail keep a stable identity;
    // see the same pattern in useGridSelection.
    const selectedThumbnailPageIdsRef = useRef(selectedThumbnailPageIds);
    selectedThumbnailPageIdsRef.current = selectedThumbnailPageIds;

    const onThumbnailItemClickRef = useRef(onThumbnailItemClick);
    onThumbnailItemClickRef.current = onThumbnailItemClick;

    const onContextMenuShowRef = useRef(onContextMenuShow);
    onContextMenuShowRef.current = onContextMenuShow;

    const allItemIds = useMemo(() => groups.flatMap((g) => g.items.map((i) => i.id)), [groups]);

    // O(1) lookup by id, used by the DragOverlay below. Previously the overlay
    // did `groups.flatMap(...).find(...)` inline on every render during a
    // drag, which is O(n) over the entire bundle.
    const itemById = useMemo(() => {
      const map = new Map<string, GridItem>();
      groups.forEach((g) => g.items.forEach((item) => map.set(item.id, item)));
      return map;
    }, [groups]);

    const { numberOfColumns, itemWidth, hasMeasuredWidth } = useSizes(containerRef);

    const {
      selectedPages,
      isContiguous,
      startOfRangeSelected,
      endOfRangeSelected,
      handleSelectPage,
      handleSelectAll,
      selectRange,
    } = useGridSelection(allItemIds, selectedThumbnailPageIds, groups, onSelectionChange);

    const [localStart, setLocalStart] = useState<number | undefined>(startOfRangeSelected);
    const [localEnd, setLocalEnd] = useState<number | undefined>(endOfRangeSelected);

    useEffect(() => {
      // keep local inputs in sync when selection changes externally
      setLocalStart(startOfRangeSelected);
      setLocalEnd(endOfRangeSelected);
    }, [startOfRangeSelected, endOfRangeSelected]);

    // compute dynamic max width for inputs based on digits length
    const inputMaxWidth = Math.max(40, String(allItemIds.length ?? allItemIds.length ?? 1).length * 12 + 8); // px

    const { onThumbnailKeyDown } = useGridKeyboardNavigation(
      groups,
      numberOfColumns,
      handleSelectPage,
      onThumbnailItemClick,
    );

    const {
      handleDragStart,
      handleDragEnd,
      handleDragCancel,
      activePageId,
      activePageIndex,
      activeGroup,
      sensors,
      measuring,
      collisionDetection,
      dropAnimation,
    } = useGridDragAndDrop({
      groups,
      setGroups,
      isDragAndDropDisabled,
      selectedThumbnailPageIds,
      onDropRequest,
    });

    useEffect(() => {
      onDragStateChange?.(activePageId !== null);
    }, [activePageId, onDragStateChange]);

    // Pages moving in the current drag: the whole selection when the grabbed page
    // is part of it, otherwise just the one page. Drives the drop marker's badge.
    const dragPageCount = activePageId
      ? selectedThumbnailPageIds.includes(activePageId)
        ? selectedThumbnailPageIds.length
        : 1
      : 0;

    const { thumbnailsViewedStatusPageIdsSet } = useThumbnailsViewedStatus();

    const { virtualRows, groupHeaderRowIndexes } = useMemo(() => {
      const virtualRows: Array<
        | { type: VirtualRowType.GroupHeader; group: GridGroup }
        | {
            type: VirtualRowType.PageIdsRow;
            group: GridGroup;
            items: GridItem[];
            rowIndex: number;
          }
      > = [];
      const groupHeaderRowIndexes: number[] = [];

      groups.forEach((group) => {
        const groupHeaderIndex = virtualRows.push({
          type: VirtualRowType.GroupHeader,
          group,
        });
        groupHeaderRowIndexes.push(groupHeaderIndex - 1);
        for (let i = 0; i < group.items.length; i += numberOfColumns) {
          virtualRows.push({
            type: VirtualRowType.PageIdsRow,
            group,
            items: group.items.slice(i, i + numberOfColumns),
            rowIndex: Math.floor(i / numberOfColumns),
          });
        }
      });
      return { virtualRows, groupHeaderRowIndexes };
    }, [groups, numberOfColumns]);

    const handleEstimateSize = useCallback(
      (index: number) => {
        const row = virtualRows[index];
        if (row.type === VirtualRowType.GroupHeader) {
          let groupHeaderSize = 20; // 10px padding top and bottom
          const previousLabels = index > 0 ? virtualRows[index - 1].group.labels : [];

          row.group.labels.forEach((label, idx) => {
            if (shouldDisplayLabel(label, previousLabels, idx)) {
              groupHeaderSize += 16; // 16px line height
            }
          });
          // For padding (20px) and button height (26px);
          if (groupHeaderSize < MIN_GROUP_HEIGHT) {
            groupHeaderSize = MIN_GROUP_HEIGHT;
          }
          return groupHeaderSize;
        }

        const itemsRowHeight = calculateA4Height(itemWidth) + PAGE_NUMBER_HEIGHT;

        return itemsRowHeight; // 270
      },
      [itemWidth, virtualRows],
    );

    const rowVirtualizer = useVirtualizer({
      count: virtualRows.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: handleEstimateSize,
      rangeExtractor: useCallback(
        (range: Range): number[] => {
          activeStickyGroupHeaderRowIndexRef.current =
            [...groupHeaderRowIndexes].reverse().find((index) => range.startIndex >= index) ?? 0;
          const next = new Set([activeStickyGroupHeaderRowIndexRef.current, ...defaultRangeExtractor(range)]);
          return [...next].sort((a, b) => a - b);
        },
        [groupHeaderRowIndexes],
      ),
      overscan: 6,
    });

    const visibleItems = rowVirtualizer.getVirtualItems();

    // Compute visible page IDs for prefetching
    const visiblePageIds = useMemo(() => {
      return visibleItems.flatMap((virtualRow) => {
        const row = virtualRows[virtualRow.index];
        if (row.type === VirtualRowType.PageIdsRow) {
          return row.items.map((item) => item.id);
        }
        return [];
      });
    }, [visibleItems, virtualRows]);

    useThumbnailPageInfoPrefetch({ visiblePageIds, allItemIds });

    useEffect(() => {
      rowVirtualizer.measure();
      // !!! Also need to add `groups` as deps.

      // Only scroll back to the current page when a resize occurred (itemWidth
      // changed), not when groups/data change, to avoid snapping back while the user
      // is browsing thumbnails. The first measurement counts as a resize even when
      // itemWidth is unchanged: a narrow rail can go from the single-column fallback
      // to a measured layout with itemWidth still clamped at MIN_COL_WIDTH, and that
      // reflow moves every row.
      const isResize =
        prevItemWidthRef.current !== null &&
        (prevItemWidthRef.current !== itemWidth || prevHasMeasuredWidthRef.current !== hasMeasuredWidth);
      prevItemWidthRef.current = itemWidth;
      prevHasMeasuredWidthRef.current = hasMeasuredWidth;

      if (isResize && currentViewportPageIndexRef.current !== null) {
        // Cancel any previously scheduled scroll-back from rapid resize events
        if (resizeScrollTimeoutRef.current !== null) {
          clearTimeout(resizeScrollTimeoutRef.current);
        }

        const pageIndex = currentViewportPageIndexRef.current;
        // Debounce: wait 100ms after last resize event for the virtualizer to settle
        resizeScrollTimeoutRef.current = setTimeout(() => {
          resizeScrollTimeoutRef.current = null;
          const rowIndex = virtualRows.findIndex(
            (row) => row.type === VirtualRowType.PageIdsRow && row.items.some((item) => item.pageIndex === pageIndex),
          );
          if (rowIndex !== -1) {
            rowVirtualizer.scrollToIndex(rowIndex, { align: 'center', behavior: 'auto' });
          }
        }, 100);
      }
    }, [itemWidth, hasMeasuredWidth, rowVirtualizer, groups, virtualRows]);

    // The scroll-back above is deliberately not cancelled when this effect re-runs
    // (groups churn during load would kill a pending recovery), so cancel it here
    // if the rail unmounts before it fires.
    useEffect(() => {
      return () => {
        if (resizeScrollTimeoutRef.current !== null) {
          clearTimeout(resizeScrollTimeoutRef.current);
        }
      };
    }, []);

    /**
     * Landing target of a cross-tab jump or a `page_id_in_viewport_on_init` deep
     * link. It arrives before the ResizeObserver has reported a width, so acting
     * on it once would resolve a row index against the single-column fallback
     * layout and jump to a row that ceases to exist the moment the real width
     * lands. Hold it and retry as the layout settles instead.
     *
     * `auto` rather than `smooth`: the virtualiser answers a smooth scrollToIndex
     * with a requestAnimationFrame retry loop against the row index it was given,
     * so a target that was stale when the call was made gets re-applied for the
     * next ~10 frames and overrides anything scheduled in the meantime.
     */
    useEffect(() => {
      if (!initialScrollToPageId || hasHandledInitialScrollRef.current || !hasMeasuredWidth) {
        return;
      }

      const rowIndex = virtualRows.findIndex(
        (row) => row.type === VirtualRowType.PageIdsRow && row.items.some((item) => item.id === initialScrollToPageId),
      );

      if (rowIndex === -1) {
        // An empty grid is still loading, so keep waiting. A populated one already
        // holds every section of this bundle, so the page is hidden by the active
        // filters and is not going to turn up — stop waiting, or clearing a filter
        // later would yank the rail to it long after the user has moved on.
        if (allItemIds.length) {
          console.warn(`No item found with pageId: ${initialScrollToPageId}`);
          hasHandledInitialScrollRef.current = true;
        }
        return;
      }

      hasHandledInitialScrollRef.current = true;

      const row = virtualRows[rowIndex];
      if (row.type === VirtualRowType.PageIdsRow) {
        const matchingItem = row.items.find((item) => item.id === initialScrollToPageId);
        if (matchingItem) {
          currentViewportPageIndexRef.current = matchingItem.pageIndex;
        }
      }

      rowVirtualizer.scrollToIndex(rowIndex, { align: 'center', behavior: 'auto' });
      lastScrollToIndexTimeRef.current = Date.now();
    }, [initialScrollToPageId, hasMeasuredWidth, virtualRows, allItemIds, rowVirtualizer]);

    const scrollToItemByPageIndex = useCallback(
      (pageIndex: number) => {
        currentViewportPageIndexRef.current = pageIndex;
        const now = Date.now();

        if (preventAutoScrollingPageRef.current && preventAutoScrollingPageRef.current.pageIndex === pageIndex) {
          preventAutoScrollingPageRef.current = null;
          return;
        }

        // Row indexes are resolved against `virtualRows`, which is built from the
        // single-column fallback until the container has been measured. Landing on
        // one of those rows would put the rail somewhere unrelated once the real
        // layout arrives; the page is recorded above, so the resize scroll-back
        // picks it up as soon as the width lands.
        if (!hasMeasuredWidth) {
          return;
        }

        const rowIndex = virtualRows.findIndex(
          (row) => row.type === VirtualRowType.PageIdsRow && row.items.some((item) => item.pageIndex === pageIndex),
        );

        if (rowIndex === -1) {
          console.warn(`No item found with pageIndex: ${pageIndex}`);
          return;
        }

        const typeOfScrollBehavior =
          now - lastScrollToIndexTimeRef.current < SCROLL_OUT_OF_THE_COMPONENT_DELAY_MS ? 'auto' : 'smooth';
        rowVirtualizer.scrollToIndex(rowIndex, {
          align: 'center',
          behavior: typeOfScrollBehavior,
        });

        lastScrollToIndexTimeRef.current = now;
      },
      [hasMeasuredWidth, virtualRows, rowVirtualizer],
    );

    const scrollToItemByPageId = useCallback(
      (pageId: string) => {
        // Find the pageIndex for this pageId to track current position
        const matchingRow = virtualRows.find(
          (row) => row.type === VirtualRowType.PageIdsRow && row.items.some((item) => item.id === pageId),
        );
        if (matchingRow && matchingRow.type === VirtualRowType.PageIdsRow) {
          const matchingItem = matchingRow.items.find((item) => item.id === pageId);
          if (matchingItem) {
            currentViewportPageIndexRef.current = matchingItem.pageIndex;
          }
        }
        const now = Date.now();

        if (preventAutoScrollingPageRef.current && preventAutoScrollingPageRef.current.pageId === pageId) {
          preventAutoScrollingPageRef.current = null;
          return;
        }

        const rowIndex = virtualRows.findIndex(
          (row) => row.type === VirtualRowType.PageIdsRow && row.items.some((item) => item.id === pageId),
        );

        if (rowIndex === -1) {
          console.warn(`No item found with pageId: ${pageId}`);
          return;
        }

        const typeOfScrollBehavior =
          now - lastScrollToIndexTimeRef.current < SCROLL_OUT_OF_THE_COMPONENT_DELAY_MS ? 'auto' : 'smooth';
        rowVirtualizer.scrollToIndex(rowIndex, {
          align: 'center',
          behavior: typeOfScrollBehavior,
        });

        lastScrollToIdTimeRef.current = now;
      },
      [virtualRows, rowVirtualizer],
    );

    useImperativeHandle(ref, () => {
      return {
        scrollToItemByPageIndex,
        scrollToItemByPageId,
      };
    }, [scrollToItemByPageIndex, scrollToItemByPageId]);

    const handleThumbnailItemClick = useCallback(
      ({ event, pageIndex, pageId }: ThumbnailItemClickProps) => {
        preventAutoScrollingPageRef.current = { pageId, pageIndex };
        currentViewportPageIndexRef.current = pageIndex;

        // if (onThumbnailItemClick && event && !(event.metaKey || event.ctrlKey || event.shiftKey)) { // OUTDATED, but maybe will need in future - prevent select with ctrl & shift key
        if (onThumbnailItemClickRef.current && event) {
          onThumbnailItemClickRef.current({ pageIndex });
        }

        if (handleSelectPage && pageId && event) {
          handleSelectPage(pageId, event);
        }
      },
      [handleSelectPage],
    );

    const handleThumbnailPointerDown = useCallback(({ /*event,*/ pageId, pageIndex }: ThumbnailPointerDownProps) => {
      preventAutoScrollingPageRef.current = { pageId, pageIndex };
      // The user has taken over. Drop any landing target still waiting on the
      // layout so it can't yank the rail out from under them later.
      hasHandledInitialScrollRef.current = true;
    }, []);

    const handleContextMenu = useCallback(
      ({ event, pageId }: ContextMenuShowProps) => {
        if (handleSelectPage && !selectedThumbnailPageIdsRef.current.includes(pageId)) {
          handleSelectPage(pageId, event);
        }
        if (onContextMenuShowRef.current) {
          onContextMenuShowRef.current({ event: event, pageId: pageId });
        }
      },
      [handleSelectPage],
    );

    return (
      <>
        <div
          ref={(el) => {
            containerRef.current = el;
            scrollRef.current = el;
          }}
          className={classNames('relative w-full overflow-y-auto pb-36', 'min-h-0 flex-1')}
        >
          <DndContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            sensors={sensors}
            collisionDetection={collisionDetection}
            measuring={measuring}
            autoScroll={{ threshold: { x: 0, y: 0.2 } }}
          >
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = virtualRows[virtualRow.index];
                const isDropRestrictedForRow =
                  isDropRestricted && activeGroup
                    ? isDropRestricted({
                        activeGroup: activeGroup,
                        group: row.group,
                      })
                    : false;
                const isSortableContextDisabled = isDragAndDropDisabled || isDropRestrictedForRow;

                if (row.type === VirtualRowType.GroupHeader) {
                  const labels = row.group.labels;
                  const previousLabels = virtualRow.index > 0 ? virtualRows[virtualRow.index - 1].group.labels : [];

                  return (
                    <div
                      key={`group-header-${row.group.id}`}
                      data-group-index={virtualRow.index}
                      data-group-id={row.group.id}
                      className={classNames(
                        'bg-fletchers-white absolute top-0 left-0 z-1 p-[8px] pr-[16px] pl-[16px]',
                        'flex w-full flex-row items-center justify-between',
                        'group min-h-12',
                      )}
                      style={{
                        // prettier-ignore
                        ...(activeStickyGroupHeaderRowIndexRef.current === virtualRow.index
                          ? {
                            position: 'sticky',
                            zIndex: 2,
                          }
                          : {
                            position: 'absolute',
                            transform: `translateY(${virtualRow.start}px)`
                          }),
                      }}
                    >
                      <div
                        className={classNames(
                          'flex min-w-0 flex-1 flex-col items-start justify-center',
                          'overflow-hidden',
                        )}
                      >
                        {labels.map((label, index) =>
                          activeStickyGroupHeaderRowIndexRef.current === virtualRow.index ||
                          shouldDisplayLabel(label, previousLabels, index) ? (
                            <GroupLabel
                              key={label.text}
                              text={label.text}
                              level={label.level}
                              onEditDate={
                                onEditClusterDate && label.type === BundleTreeTypes.CLUSTER
                                  ? () => onEditClusterDate(label)
                                  : undefined
                              }
                            />
                          ) : null,
                        )}
                      </div>
                      <div
                        className={classNames(
                          'flex shrink-0 flex-row items-center justify-end',
                          'hidden group-hover:flex',
                        )}
                      >
                        <Button
                          outlined
                          className="!h-[28px] !text-[12px]"
                          onClick={() => handleSelectAll(row.group.id)}
                        >
                          Select all
                        </Button>
                      </div>
                    </div>
                  );
                }

                return (
                  <SortableContext
                    key={`item-row-${row.group.id}-${row.rowIndex}`}
                    items={row.group.items.map((i) => i.id)}
                    strategy={staticSortingStrategy}
                    disabled={isSortableContextDisabled}
                  >
                    <div
                      className={classNames('absolute top-0 left-0 m-0 grid w-full items-center justify-center', {
                        'bg-fixed" bg-[repeating-linear-gradient(135deg,_var(--color-fletchers-yellow-500)_0,_var(--color-fletchers-yellow-500)_5px,_transparent_0,_transparent_50%)] bg-[size:15px_15px]':
                          isDropRestrictedForRow,
                      })}
                      style={{
                        transform: `translateY(${virtualRow.start}px)`,
                        gridTemplateColumns: `repeat(${numberOfColumns}, minmax(${MIN_COL_WIDTH}px, ${MAX_COL_WIDTH}px))`,
                        gap: `${COL_GAP}px`,
                        padding: `4px ${COL_GAP}px 4px 8px`,
                      }}
                    >
                      {row.items.map((item) => {
                        const isSelected = selectedPages?.includes(item.id);
                        const isCutOut = bundleCutOutThumbnailIndexes?.includes(item.pageIndex);
                        const isViewed = thumbnailsViewedStatusPageIdsSet.has(item.id);
                        const isDragItemInSelectedList = activePageId
                          ? selectedThumbnailPageIds.includes(activePageId)
                          : false;

                        return (
                          <SortableThumbnailPage
                            key={item.id}
                            pageId={item.id}
                            pageIndex={item.pageIndex}
                            activePageIndex={activePageIndex}
                            dropIndicatorCount={dragPageCount}
                            thumbnailUri={item.pageData.thumbnailUri}
                            documentName={item.pageData.documentName}
                            pageWidth={item.pageData.pageDimensions.width}
                            pageHeight={item.pageData.pageDimensions.height}
                            isSelected={isSelected}
                            isCutOut={isCutOut}
                            isViewed={isViewed}
                            isDragItemInSelectedList={isDragItemInSelectedList}
                            isHighRelevance={item.pageData.highRelevance}
                            isInLightBundle={item.pageData.inLightBundle}
                            isInChronology={Boolean(item.pageData.chronologyEntryIds?.length)}
                            activeBundleType={activeBundleType}
                            onThumbnailKeyDown={onThumbnailKeyDown}
                            onThumbnailItemClick={handleThumbnailItemClick}
                            onThumbnailPointerDown={handleThumbnailPointerDown}
                            onContextMenuShow={handleContextMenu}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                );
              })}
            </div>
            <DragOverlay dropAnimation={dropAnimation}>
              {activePageId && (
                <ThumbnailPageOverlay
                  item={itemById.get(activePageId)}
                  activeBundleType={activeBundleType}
                  selectedIdsLength={
                    selectedThumbnailPageIds.includes(activePageId) ? selectedThumbnailPageIds.length : 0
                  }
                />
              )}
            </DragOverlay>
          </DndContext>
        </div>
        {selectedPages && selectedPages.length > 1 && isContiguous && (
          <div
            className={classNames(
              'flex items-center justify-center',
              'bg-fletchers-white',
              'border-fletchers-gray-110 border-t',
              'px-3 py-[8px]',
              'text-fletchers-black-900 text-[14px] font-[400]',
            )}
          >
            {isContiguous && (
              <div className="flex flex-row flex-wrap items-center justify-center gap-1.5">
                <span>Selected</span>
                <InputNumber
                  min={1}
                  max={Math.max(1, Math.max(localEnd ?? 0, endOfRangeSelected ?? 0) - 1)}
                  value={localStart}
                  step={1}
                  useGrouping={false}
                  className="!w-auto"
                  pt={{
                    input: {
                      root: {
                        style: { maxWidth: `${inputMaxWidth}px` },
                        className: '!h-[22px] !text-[14px] !text-fletchers-black-900 !font-[400]',
                      },
                    },
                  }}
                  onChange={(e) => {
                    const newStart = Number(e.value ?? e);
                    if (!Number.isFinite(newStart)) return;
                    setLocalStart(Math.max(1, Math.min(newStart, allItemIds.length)));
                  }}
                  onKeyDown={(ev: React.KeyboardEvent) => {
                    if (ev.key === 'Enter' && localStart != null) {
                      const s = Math.max(1, Math.min(localStart, localEnd ?? localStart));
                      selectRange(s - 1, (localEnd ?? s) - 1);
                      scrollToItemByPageIndex(s - 1);
                    }
                  }}
                />
                <span>to</span>
                <InputNumber
                  min={1}
                  max={allItemIds.length}
                  value={localEnd}
                  step={1}
                  useGrouping={false}
                  className="!w-auto"
                  pt={{
                    input: {
                      root: {
                        style: { maxWidth: `${inputMaxWidth}px` },
                        className: '!h-[22px] !text-[14px] !text-fletchers-black-900 !font-[400]',
                      },
                    },
                  }}
                  onChange={(e) => {
                    const newEnd = Number(e.value ?? e);
                    if (!Number.isFinite(newEnd)) return;
                    setLocalEnd(Math.max(1, Math.min(newEnd, allItemIds.length)));
                  }}
                  onKeyDown={(ev: React.KeyboardEvent) => {
                    if (ev.key === 'Enter' && localEnd != null) {
                      const en = Math.min(allItemIds.length, Math.max(localEnd, 1));
                      // If the new end is less than current edited start, on commit set start = end - 1
                      let startToUse = localStart ?? startOfRangeSelected;
                      if (en <= startToUse) {
                        startToUse = Math.max(1, en - 1);
                        setLocalStart(startToUse);
                      }
                      selectRange(startToUse - 1, en - 1);
                      scrollToItemByPageIndex(en - 1);
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}
      </>
    );
  },
);
